/**
 * FLY NEXT MISSION: what the squadron has you doing today.
 *
 * A career does not pick its own missions — *"In Career play, you are given
 * much less control over the mission conditions"* (manual, "Career Play") — so
 * this draws one from the port's mission catalog and pins every option the
 * career already knows: the era from the calendar, the region from the
 * squadron's aerodrome, the side from the service, and the aeroplane from the
 * squadron's equipment or the pilot's own personal machine.
 *
 * Deterministic by construction: every draw comes from one `MissionDraw` seeded
 * with the career's seed and salted with the mission number, so the same career
 * state always produces the same assignment, and re-generating it after a
 * reload gives back the identical sortie.
 *
 * **The mission mix is port-side.** Nothing decoded says how the original chose
 * between mission types; this weights patrols heaviest, keeps Zeppelin hunts to
 * the Allied side (the airships were German, and this port has no Allied
 * airship to hunt), and leaves the ace duel rare so that meeting a famous ace
 * stays an event.
 */

import { MissionDraw } from "../mission/mission-build.ts";
import { generateMission } from "../mission/mission-catalog.ts";
import { acesFor, aircraftLabel, opposingNation, type FamousAce, type Nation } from "../mission/mission-roster.ts";
import type { MissionSpec } from "../mission/mission-spec.ts";
import { REGION_LABELS } from "../mission/mission-world.ts";
import { eraOf, formatCareerDate, type CareerDate } from "./calendar.ts";
import { currentAircraft, squadronOf, totalVictories, type CareerState } from "./career-state.ts";
import { flightRoleOf, flightSizeFor, rankOf, type FlightRole } from "./service.ts";

/** Catalog ids weighted per side. Repetition is the weight. */
const MISSION_MIX: Readonly<Record<Nation, readonly string[]>> = {
  allied: [
    "patrol",
    "patrol",
    "patrol",
    "patrol",
    "escort-recon",
    "escort-recon",
    "balloon-busting",
    "balloon-busting",
    "scramble",
    "hunt-zeppelin",
    "dogfight-ace",
  ],
  german: [
    "patrol",
    "patrol",
    "patrol",
    "patrol",
    "escort-recon",
    "escort-recon",
    "escort-recon",
    "balloon-busting",
    "scramble",
    "dogfight-ace",
  ],
};

/** An ace only sends a note to somebody who is "doing exceptionally well". */
const CHALLENGE_VICTORIES = 10;
const CHALLENGE_CHANCE = 0.12;

const SECTOR_LETTERS = "ABCDEFGH";

export interface AceChallenge {
  readonly aceId: string;
  readonly aceName: string;
  /** `"C-3"` — the manual's own grid reference style for the five regional maps. */
  readonly sector: string;
  /** Verbatim `PS.EXE` challenge note. */
  readonly note: string;
}

export interface CareerAssignment {
  /** 1 for a pilot's first sortie. */
  readonly missionNumber: number;
  readonly date: CareerDate;
  readonly catalogId: string;
  readonly spec: MissionSpec;
  readonly flightRole: FlightRole;
  readonly flightSize: number;
  readonly challenge: AceChallenge | undefined;
  /** The orders as the aerodrome reads them out. */
  readonly orders: readonly string[];
  /** What this port does not model about a career assignment. */
  readonly portNotes: readonly string[];
}

function challengeFor(career: CareerState, draw: MissionDraw): AceChallenge | undefined {
  if (totalVictories(career) < CHALLENGE_VICTORIES) return undefined;
  if (!draw.chance(CHALLENGE_CHANCE)) return undefined;
  const ace: FamousAce = draw.pick(acesFor(opposingNation(career.nation)));
  const sector = `${draw.pick([...SECTOR_LETTERS])}-${draw.int(1, 8)}`;
  return {
    aceId: ace.id,
    aceName: ace.name,
    sector,
    note: `${ace.name} has challenged you to a one-on-one dogfight. His note instructs you to meet in sector ${sector}.`,
  };
}

const FLIGHT_SIZE_NOTE =
  "Rank privileges are reported but not yet flown: the mission generators build their own flight sizes, so a Captain's four-aircraft flight is not assembled for him. The rank still gates the personal aircraft and transfers, which are real.";

const CALENDAR_NOTE =
  "The sortie is generated for the war era the career date falls in (early / mid / late), not for the exact month — the generators bucket aircraft by era, while the squadron's own equipment does follow the month.";

const ACE_PRESENCE_NOTE =
  "Which ace you meet is not filtered by the calendar. The manual describes ace encounters as depending on \"the known location of individual aces at the time\", and this port has no model of where each ace is in which month, so an ace duel can pair you with someone whose war was over or had not started.";

function ordersFor(career: CareerState, role: FlightRole, size: number): readonly string[] {
  const unit = squadronOf(career);
  const rank = rankOf(career.nation, career.rank);
  const mount = currentAircraft(career);
  const personal = career.personalAircraft ? " — your own machine" : "";
  const command =
    role === "leader"
      ? `You lead the flight: up to ${size} aircraft, and their orders are yours.`
      : "You fly as wingman. Hold formation and follow the flight leader.";
  return [
    `${formatCareerDate(career.date)}. ${unit.name}, ${unit.aerodrome} aerodrome, in the ${REGION_LABELS[unit.region]} region.`,
    `${rank.title} ${career.name}, ${aircraftLabel(mount)}${personal}.`,
    command,
  ];
}

export interface AssignmentOptions {
  /**
   * `PS.EXE`'s `START AT YOUR AERODROME` (`mission-options.ts`), read from the
   * player's Realism Panel exactly as `careerSortie` already reads `tier`,
   * `realism` and `combatLevel` for this same sortie — except this one has to
   * reach the generator itself rather than being stamped on afterwards, since
   * it changes where the flight starts rather than how it flies.
   */
  readonly aerodromeStart?: boolean;
}

/**
 * The next sortie, or `undefined` for a pilot who cannot fly one — dead,
 * grounded, retired, in hospital or in a prison camp.
 */
export function nextAssignment(career: CareerState, options: AssignmentOptions = {}): CareerAssignment | undefined {
  if (career.status !== "active") return undefined;
  const missionNumber = career.missionsFlown + 1;
  const draw = new MissionDraw(career.seed, `assignment:${missionNumber}:${career.declinedChallenges}`);
  const challenge = challengeFor(career, draw);
  const catalogId = challenge ? "dogfight-ace" : draw.pick(MISSION_MIX[career.nation]);
  const spec = generateMission(catalogId, {
    seed: draw.int(1, 0x3fffffff),
    era: eraOf(career.date),
    region: squadronOf(career).region,
    nation: career.nation,
    playerAircraft: currentAircraft(career),
    aceId: challenge?.aceId,
    aerodromeStart: options.aerodromeStart,
  });
  const role = flightRoleOf(career.rank);
  const size = flightSizeFor(career.rank);
  return {
    missionNumber,
    date: career.date,
    catalogId,
    spec,
    flightRole: role,
    flightSize: size,
    challenge,
    orders: ordersFor(career, role, size),
    portNotes: catalogId === "dogfight-ace"
      ? [FLIGHT_SIZE_NOTE, CALENDAR_NOTE, ACE_PRESENCE_NOTE]
      : [FLIGHT_SIZE_NOTE, CALENDAR_NOTE],
  };
}

/**
 * Turning a challenge down. The manual is explicit that this is allowed and
 * costs nothing — *"You may choose to ignore or answer these challenges"* — so
 * declining simply redraws the day's orders, deterministically, rather than
 * rolling for a new mission behind the player's back.
 */
export function declineChallenge(career: CareerState): CareerState {
  return { ...career, declinedChallenges: career.declinedChallenges + 1 };
}
